import React, { useState, useEffect } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { m, AnimatePresence } from "framer-motion";
import {
  EyeIcon,
  CreditCardIcon,
  CheckCircleIcon,
  FLOATING_ICON_MAP,
} from "../MarketingIcons";

const fadeIn2 = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.2 },
};

const Hero = ({ hero }) => {
  const [showCrossout, setShowCrossout] = useState(false);
  const [showReplacement, setShowReplacement] = useState(false);
  const [showAdditionalContent, setShowAdditionalContent] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  useEffect(() => {
    if (isImageLoaded) {
      const t1 = setTimeout(() => setShowCrossout(true), 2000);
      const t2 = setTimeout(() => setShowReplacement(true), 3500);
      const t3 = setTimeout(() => setShowAdditionalContent(true), 4000);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
      };
    }
  }, [isImageLoaded]);

  const TopLeftIcon = FLOATING_ICON_MAP[hero.floating.topLeft.icon] || EyeIcon;
  const BottomIcon = FLOATING_ICON_MAP[hero.floating.bottom.icon] || CreditCardIcon;

  return (
    <m.section
      className='py-32 md:py-12 xs:pt-0 text-left md:text-center bg-white relative overflow-visible hero-section-container'
      {...fadeIn2}
    >
      <Container className='overflow-visible'>
        <Row className='items-center overflow-visible '>
          <Col lg={7} md={12} className='mb-12 mb-lg-0 overflow-visible '>
            <h1 className='font-["Oxanium"] text-left md:text-center text-3xl sm:text-5xl xs:!text-[44px] lg:text-6xl font-bold md:!px-0 mb-6 text-gray-900'>
              {hero.headlinePrefix}
              <span className='inline-flex flex-col overflow-hidden relative'>
                <div className='relative h-auto'>
                  <AnimatePresence mode='wait'>
                    {!showReplacement ? (
                      <m.span
                        key='original'
                        className='inline-block'
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.7 }}
                      >
                        {hero.headlineOriginal}
                        {showCrossout && (
                          <svg
                            className='absolute top-1/2 left-0 w-full h-8 -translate-y-1/2 z-10 overflow-visible'
                            xmlns='http://www.w3.org/2000/svg'
                            viewBox='0 0 400 10'
                            style={{ willChange: "transform" }}
                          >
                            <m.path
                              d='M0,10 C125,-10 275,35 400,-5'
                              fill='none'
                              stroke='#991b1b'
                              strokeWidth='3'
                              strokeLinecap='butt'
                              initial={{ pathLength: 0 }}
                              animate={{ pathLength: 1 }}
                              transition={{ duration: 1.2, ease: "easeOut", delay: 0.2 }}
                              style={{
                                transform: "translateZ(0)",
                                willChange: "stroke-dashoffset",
                              }}
                            />
                          </svg>
                        )}
                      </m.span>
                    ) : (
                      <m.span
                        key='replacement'
                        className='text-red-800 inline-block'
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, ease: "easeIn" }}
                      >
                        {hero.headlineReplacement}
                      </m.span>
                    )}
                  </AnimatePresence>
                </div>
              </span>
            </h1>
            <h2 className='text-lg text-gray-600 mb-6 mr-10 md:!mx-auto'>
              {hero.subHeadline}
            </h2>

            <div className='min-h-[160px] md:flex md:mx-auto  justify-center'>
              {showReplacement && (
                <div
                  className='flex flex-col gap-3 mt-0 mb-8 '
                  style={{
                    animation: "fadeInFromTop 1s ease",
                    animationDelay: "2s",
                    animationFillMode: "both",
                    opacity: 0,
                    transform: "translateY(-10px)",
                  }}
                >
                  {hero.valueProps.map((vp, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-2 group hover:text-[#6a604d]${i === 0 ? " md:mt-4" : ""}`}
                    >
                      <CheckCircleIcon />
                      <span className='text-gray-500 group-hover:text-[#6a604d] pointer-events-none'>
                        {vp}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className='flex flex-wrap gap-4 min-h-[40px] md:mx-auto md:justify-center'>
              {showAdditionalContent && (
                <Link
                  to='/#inaq-marketing-services'
                  onClick={(e) => {
                    e.preventDefault();
                    const element = document.getElementById("inaq-marketing-services");
                    if (element) {
                      element.scrollIntoView({ behavior: "smooth" });
                    }
                  }}
                  className='inline-flex items-center text-gray-900 font-semibold  hover:text-[#6a604d] transition-colors group text-lg'
                  style={{
                    animation: "fadeInFromTop 1s ease",
                    animationFillMode: "both",
                    animationDelay: "2.5s",
                    opacity: 0,
                    transform: "translateY(-20px)",
                  }}
                >
                  {hero.ctaLabel}{" "}
                  <span className='ml-2 inline-block transition-transform duration-200 group-hover:translate-x-1'>
                    ⇾
                  </span>
                </Link>
              )}
            </div>
          </Col>
          <Col lg={5} md={12} className='overflow-visible'>
            <div className='image-container relative'>
              <div
                className='absolute inset-0 transition-opacity duration-50'
                style={{
                  opacity: isImageLoaded ? 0.5 : 1,
                  backgroundColor: "white",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              ></div>

              <img
                src={hero.image.src}
                alt={hero.image.alt}
                className='w-full h-auto rounded-xs object-cover relative z-10 transition-opacity duration-50'
                width='458'
                height='458'
                loading='eager'
                fetchpriority='high'
                decoding='async'
                style={{ opacity: isImageLoaded ? 1 : 0 }}
                onLoad={() => setIsImageLoaded(true)}
              />

              <div className='floating-box floating-box-left'>
                <div className='flex items-center gap-3'>
                  <div className='icon-circle icon-circle-primary'>
                    <TopLeftIcon />
                  </div>
                  <p className='text-sm font-medium m-0 text-inaqpurple'>
                    {hero.floating.topLeft.label}
                  </p>
                </div>
              </div>
              <div className='floating-box floating-box-bottom'>
                <div className='flex items-center gap-3'>
                  <div className='icon-circle icon-circle-success'>
                    <BottomIcon />
                  </div>
                  <p className='text-sm font-medium m-0 text-black'>
                    {hero.floating.bottom.label}
                  </p>
                </div>
              </div>
              <svg
                className='connecting-line block md:hidden'
                width='100%'
                height='100%'
                viewBox='0 0 100 100'
                preserveAspectRatio='none'
              >
                <defs>
                  <filter
                    id='pencilTexture'
                    x='-20%'
                    y='-20%'
                    width='140%'
                    height='140%'
                  >
                    <feTurbulence
                      type='fractalNoise'
                      baseFrequency='0.02'
                      numOctaves='2'
                      result='noise'
                    />
                    <feDisplacementMap
                      in='SourceGraphic'
                      in2='noise'
                      scale='5'
                      xChannelSelector='R'
                      yChannelSelector='G'
                    />
                    <feGaussianBlur stdDeviation='0.10' />
                  </filter>
                </defs>
                <path
                  d='M40,12
        Q65,15 80,20
        T85,50
        Q70,65 85,70
        T65,98'
                  stroke='white'
                  strokeWidth='1.2'
                  fill='none'
                  strokeLinecap='round'
                  filter='url(#pencilTexture)'
                />
              </svg>
            </div>
          </Col>
        </Row>
      </Container>
      <div className='absolute inset-y-0 right-1/2 -z-10 -mr-96 w-[200%] origin-top-right skew-x-[-30deg] bg-white  sm:-mr-80 lg:-mr-96'></div>
    </m.section>
  );
};

export default Hero;
